import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import React from "react";
import i18n from "../../i18n.config";
import ToothFairyToast from "../Components/ToothFairyComponents/ToothFairyToast";
import { GenerationDomains, BaseModelType } from "../models";
import countries from "./countries.json";

const date = new Date();
const timezoneOffset = date.getTimezoneOffset() * -1;

// Valid referral codes
const VALID_REFERRAL_CODES = [
  "TF_TEAM_REFERRAL",
  "LUKE_MARSHALL",
  "SIMON_KRISS",
  "LUX_TECH",
];

const newsletterConsent = {
  // Bump this when the consent terms change so we can audit exactly
  // which terms version each user agreed to (ACMA record-keeping).
  termsVersion: "v1",
  // Static, i18n keys for the consent terms shown in onboarding/popup/settings.
  terms: {
    what: "newsletter.terms.what",
    who: "newsletter.terms.who",
    howLong: "newsletter.terms.howLong",
    howWithdraw: "newsletter.terms.howWithdraw",
  },
};

const config = {
  testAnExample: {
    isCategoriesAndToolbarVisibile: false,
  },
};
const defaultDocumentID = "newDocID";
const notificationConfig = {
  successColor: "green",
  dangerColor: "red",
  warningColor: "orange",
  normalColor: "gray",
  placement: "bottom",
  duration: 3000,
  animationType: "slide-in",
  animationDuration: 100,
  offset: 10,
  icon: <AppIcon color={"white"} icon={AppAssets.icons.ai} />,
  successIcon: <AppIcon icon={AppAssets.icons.check} />,
  dangerIcon: <AppIcon icon={AppAssets.icons.issue} />,
  warningIcon: <AppIcon icon={AppAssets.icons.risk} />,
  textStyle: { fontSize: 16 },
  renderToast: (toastOptions) => {
    return <ToothFairyToast {...toastOptions} />;
  }, // offset for both top and bottom toasts
};
const baseTopics = [
  // {
  //   label: "WEBSITE",
  //   backgroundColor: "#00A6A6",
  //   type: "topic",
  // },
  // {
  //   label: "PRIVATE",
  //   backgroundColor: "#EFCA08",
  //   type: "topic",
  // },
  {
    label: "DEFAULT",
    backgroundColor: "#FF934F",
    type: "topic",
  },
];

const defaultProductivityIntents = [
  {
    label: "TASK",
    backgroundColor: "#00A6A6",
  },
  {
    label: "REQUIREMENT",
    backgroundColor: "#EFCA08",
  },
  {
    label: "ISSUE",
    backgroundColor: "#FF934F",
  },
  {
    label: "RISK",
    backgroundColor: "#e63f08",
  },
  {
    label: "INSIGHT",
    backgroundColor: "#C2E812",
  },
  {
    label: "DECISION",
    backgroundColor: "#AA3E98",
  },
  {
    label: "MEETING",
    backgroundColor: "#FB9F89",
  },
  {
    label: "OPPORTUNITY",
    backgroundColor: "#77b58d",
  },
  {
    label: "REMINDER",
    backgroundColor: "#9297C4",
  },
  {
    label: "UNDEFINED",
    backgroundColor: "#BFCBC2",
  },
];

const defaultSentimentIntents = [
  {
    label: "POSITIVE",
    backgroundColor: "#7ccf00",
  },
  {
    label: "NEUTRAL",
    backgroundColor: "#c5cf00",
  },
  {
    label: "NEGATIVE",
    backgroundColor: "#cf9400",
  },
];

export const defaultConsumptionObject = {
  generator: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  questioner: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  translator: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  answerer: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  parser: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  tokenizer: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  embedder: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  detector: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
  },
  chatter: {
    requests: 0,
    words: 0,
    tokens: 0,
    sentences: 0,
    apiRequests: 0,
  },
  lastUpdate: null,
};

const generateBaseIntentsFromBaseModel = (model) => {
  switch (model) {
    case "none":
      return [];
    case "sentiment":
      return defaultSentimentIntents;
    case "productivity":
      return defaultProductivityIntents;
    default:
      break;
  }
};
const sideMenuWidth = 200;
//
const availableLanguages = [
  { id: "en", name: "English", nativeName: "English" },
  { id: "it", name: "Italian", nativeName: "Italiano" },
  { id: "fr", name: "French", nativeName: "Français" },
  { id: "de", name: "German", nativeName: "Deutsch" },
  { id: "es", name: "Spanish", nativeName: "Español" },
  { id: "nl", name: "Dutch", nativeName: "Nederlands" },
  { id: "fi", name: "Finnish", nativeName: "Suomi" },
  { id: "pt", name: "Portuguese", nativeName: "Português" },
  { id: "pl", name: "Polish", nativeName: "Polski" },
];
const availableRCLanguages = [
  ...availableLanguages,
  { id: "def", name: "Default to question language" },
];

const mapKeysToNames = (key, isBatch) => {
  switch (key) {
    case "nlp":
      return "Sentiment reviewer";
    case "nlpTraining":
      return "Sentiment reviewer";
    case "rc":
      return isBatch ? "Business analyst" : "Reasoning";
    case "readComprehensionTraining":
      return isBatch ? "Business analyst" : "Reasoning";
    case "gn":
      return isBatch ? "Content creator" : "DPO";
    case "generativeTraining":
      return isBatch ? "Content creator" : "DPO";
    case "conversationalTraining":
      return isBatch ? "Agent" : "Supervised";
    case "cn":
      return isBatch ? "Agent" : "Supervised";
    case "grpoTraining":
    case "grpo":
      return "GRPO";
    case "grpoTraining+rlvr":
    case "rlTask":
    case "rl":
      return "RLVR";
    case "ktoTraining":
    case "kto":
      return "KTO";
    case "continuedPretraining":
    case "cpt":
      return "CPT";
    case "bm":
      return "benchmark";
    case "abm":
      return "benchmark";
    default:
      return "";
  }
};
const onboardingData = {
  // change
  name: {
    icon: AppAssets.icons.businessAnalyst,
    fieldInitialValue: { name: "" },
    fieldName: "name",
    fieldPlaceholder: "Your full name",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        name: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.name.label")),
      });
    },
    title: "What's your full name?",
  },
  language: {
    icon: AppAssets.icons.language,
    fieldInitialValue: { language: i18n.language || "en" },
    fieldName: "language",
    fieldOptions: availableLanguages,
    fieldPlaceholder: "Select language",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        language: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.language.label")),
      });
    },
    title: "Choose your language",
  },
  timezone: {
    icon: AppAssets.icons.timezoneOnboarding,
    fieldInitialValue: {
      timezone: AppUtilities.timezoneData().find(
        (u) => u.timezoneOffset === timezoneOffset,
      ).id,
    },
    fieldName: "timezone",
    fieldOptions: AppUtilities.timezoneData(),
    fieldPlaceholder: "Select timezone",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        timezone: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.timezone.label")),
      });
    },
    title: "Let's align the clocks",
  },
  theme: {
    icon: AppAssets.icons.paintRoller,
    fieldInitialValue: {
      id: "light",
      theme: "light",
      title: "Light",
      internalTitle: "Light - VC mode",
    },
    fieldName: "theme",
    fieldOptions: [
      { id: "light", title: "Light", internalTitle: "Light - VC mode" },
      { id: "dark", title: "Dark", internalTitle: "Dark - Founder mode" },
      // { id: "blue", title: "ToothFairyAI" },
    ],
    fieldPlaceholder: "Select theme",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        theme: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.theme.label")),
      });
    },
    title: "Choose your theme",
  },
  referral: {
    icon: AppAssets.icons.referral,
    fieldInitialValue: {
      sourceCode: "",
      referralCode: "",
      eventCode: "",
      subscribedToNewsletter: false,
    },
    fieldName: "referralCode",
    fieldPlaceholder: "Referral code",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        sourceCode: AppForms.yup
          .string()
          .nullable()
          .label(i18n.t("onboarding.referral.sourceCodeLabel")),
        referralCode: AppForms.yup.string().when("sourceCode", {
          is: "PARTNER",
          then: AppForms.yup
            .string()
            .required()
            .label(i18n.t("onboarding.referral.partnerCodeLabel")),
          otherwise: AppForms.yup.string().nullable(),
        }),
        eventCode: AppForms.yup.string().when("sourceCode", {
          is: "EVENT",
          then: AppForms.yup
            .string()
            .label(i18n.t("onboarding.referral.eventCodeLabel")),
          otherwise: AppForms.yup.string().nullable(),
        }),
        subscribedToNewsletter: AppForms.yup.boolean().default(false),
      });
    },
    title: "Who told you about us?",
    sourceOptions: [
      { id: null, name: "Please select one option" },
      { id: "PARTNER", name: "Partner (promo available)" },
      { id: "EVENT", name: "Event (promo available)" },
      { id: "WEBSITE", name: "Website" },
      { id: "SOCIAL_MEDIA", name: "Social Media" },
      { id: "GOOGLE", name: "Google" },
      { id: "COLD_OUTREACH", name: "Cold Outreach" },
      { id: "ADVERTISING", name: "Advertising" },
      { id: "WORD_OF_MOUTH", name: "Word of Mouth" },
      { id: "OTHER", name: "Other" },
    ],
  },
  workspaceName: {
    icon: AppAssets.icons.enableAI,
    fieldInitialValue: { workspaceName: "" },
    fieldName: "workspaceName",
    fieldPlaceholder: "AI workspace name",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        workspaceName: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.workspaceName.label")),
      });
    },
    title: "Name of your AI workspace",
  },
};

const workspaceCreationData = {
  workspaceType: {
    icon: AppAssets.icons.enableAI,
    fieldInitialValue: { workspaceType: "flat" },
    fieldName: "workspaceType",
    fieldOptions: [
      { id: "hierarchical", title: "Hierarchical" },
      { id: "flat", title: "Flat" },
    ],
    fieldPlaceholder: "Select intent structure",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        workspaceType: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.workspaceIntentStructure")),
      });
    },
    title: "Choose intent structure",
  },
  workspaceMed: {
    icon: AppAssets.icons.paintRoller,
    fieldInitialValue: { workspaceMed: false },
    fieldName: "workspaceMed",
    fieldOptions: [
      { id: "false", title: "False" },
      { id: "true", title: "True" },
    ],
    fieldPlaceholder: "The workspace needs medical NERs",
    get fieldValidation() {
      return AppForms.yup.object().shape({
        workspaceType: AppForms.yup
          .string()
          .required()
          .label(i18n.t("onboarding.workspaceMedicalSetting")),
      });
    },
    title: "Choose workspace medical settings",
  },
  workspaceBaseModel: {
    fieldName: "baseModel",
    fieldInitialValue: { baseModel: "NONE" },
    fieldOptions: [
      ...Object.keys(BaseModelType).map((key) => ({
        id: key,
        title: key,
      })),
    ],
    title: "Choose a base model",
  },
  workspaceDomain: {
    fieldName: "domain",
    fieldInitialValue: { domain: "DEFAULT" },
    fieldOptions: [
      ...Object.keys(GenerationDomains).map((key) => ({
        id: key,
        title: key,
      })),
    ],
    title: "Choose your domain",
  },
  subscriptionType: {
    fieldName: "subscriptionType",
    fieldInitialValue: { subscriptionType: "base" },
    fieldOptions: [
      { id: "base", title: "Base" },
      { id: "pro", title: "Individual" },
      { id: "business", title: "Business" },
      { id: "enterprise", title: "Enterprise" },
    ],
    title: "Choose a subscription",
  },
};
const defaultModerations = {
  illegal:
    "Illegal activity, including content that promotes or facilitates the sale or use of illegal or regulated substances",
  "child abuse":
    "child sexual abuse material or any content that exploits or harms children.",
  "hate violence harassment":
    "Generation of hateful, harassing, or violent content: content that expresses, incites, or promotes hate based on identity, content that intends to harass, threaten, or bully an individual, content that promotes or glorifies violence or celebrates the suffering or humiliation of others.",
  malware:
    "Generation of malware: content that attempts to generate code that is designed to disrupt, damage, or gain unauthorized access to a computer system.",
  "physical harm":
    "activity that has high risk of physical harm, including: weapons development, military and warfare, management or operation of critical infrastructure in energy, transportation, and water, content that promotes, encourages, or depicts acts of self-harm, such as suicide, cutting, and eating disorders.",
  "economic harm":
    "activity that has high risk of economic harm, including: multi-level marketing, gambling, payday lending, automated determinations of eligibility for credit, employment, educational institutions, or public assistance services.",
  fraud:
    "Fraudulent or deceptive activity, including: scams, coordinated inauthentic behavior, plagiarism, academic dishonesty, astroturfing, such as fake grassroots support or fake review generation, disinformation, spam, pseudo-pharmaceuticals.",
  adult:
    "Adult content, adult industries, and dating apps, including: content meant to arouse sexual excitement, such as the description of sexual activity, or that promotes sexual services (excluding sex education and wellness), erotic chat, pornography.",
  political:
    "Political campaigning or lobbying, by: generating high volumes of campaign materials, generating campaign materials personalized to or targeted at specific demographics, building conversational or interactive systems such as chatbots that provide information about campaigns or engage in political advocacy or lobbying, building products for political campaigning or lobbying purposes.",
  privacy:
    "Activity that violates people's privacy, including: tracking or monitoring an individual without their consent, facial recognition of private individuals, classifying individuals based on protected characteristics, using biometrics for identification or assessment, unlawful collection or disclosure of personal identifiable information or educational, financial, or other protected records.",
  "unqualified law":
    "Engaging in the unauthorized practice of law, or offering tailored legal advice without a qualified person reviewing the information.",
  "unqualified financial":
    "Offering tailored financial advice without a qualified person reviewing the information.",
  "unqualified health":
    "Telling someone that they have or do not have a certain health condition, or providing instructions on how to cure or treat a health condition.",
  "unqualified education":
    "Offering tailored educational advice without a qualified person reviewing the information.",
  "prompt injection":
    "Attempts to manipulate or bypass the system's programming or operational guidelines, including: instructions asking the agent to ignore or discolse its own safety, security, ethical guidelines, or designed operational parameters, crafting prompts to produce output that would otherwise be restricted or filtered, exploiting any system vulnerabilities to alter the agent's functions, or soliciting information on how to modify the agent's behavior against protocol.",
};
const appButtonHeight = 27;
const saveTimer = 2000;
const screenResponsiveWidth = 888;
const chartMaxHeight = 600;
const doughnutChartMaxHeight = 420;
const inactiveTimeout = 1000 * 60 * 30;
const initialWorkspaceValues = {
  workspaceName: "",
  workspaceType: "flat",
  workspaceMed: false,
  subscriptionType: "base",
  logo: null,
  enforceRegionalModels: false,
  blockedModels: [],
  defaultChatAgent: null,
  billingProvider: "STRIPE",
  supportHours: 0,
  supportHoursUsed: 0,
  reservedComputeAllocation: false,
};
const NLP_OPTIONS = [
  {
    id: "IMP_SELF",
    label: "SelfNLPCaption",
    logicalPropertyName: "isImplicitSelfEnabled",
  },
  {
    id: "SENT_SPLIT",
    label: "SplitNLPCaption",
    logicalPropertyName: "isSentenceSplitEnabled",
  },
];

const MRC_OPTIONS = [
  // {
  //   id: "QA_AS_NER",
  //   label: "qaAsNER",
  //   logicalPropertyName: "answerAsNER",
  // },
  // {
  //   id: "QA_BY_PAR",
  //   label: "qaByPar",
  //   logicalPropertyName: "answerByParagraph",
  // },
  {
    id: "UNI_QA",
    label: "uniQA",
    logicalPropertyName: "uniqueAnswer",
  },
  {
    id: "BLEND_QA",
    label: "blenderQA",
    logicalPropertyName: "qaBlender",
  },
  {
    id: "COMMON_KB",
    label: "knReply",
    logicalPropertyName: "qaAiKnowledge",
  },
];

const DOWNLOAD_OPTIONS = [
  {
    id: "NER_DATA",
    label: "NER Dataset",
    filename: "ner_dataset.json",
    type: "NLP",
  },
  {
    id: "INTENT_DATA",
    label: "Intent Dataset",
    filename: "intent_dataset.json",
    type: "NLP",
  },
  {
    id: "TEST_NER_DATA",
    label: "Validation NER Dataset",
    filename: "ner_test_dataset.json",
    type: "NLP",
  },
  {
    id: "TEST_INTENT_DATA",
    label: "Validation Intent Dataset",
    filename: "intent_test_dataset.json",
    type: "NLP",
  },
  {
    id: "MODEL",
    label: "Model",
    type: "NLP",
    filename: "model.tar.gz",
  },
  {
    id: "META_DATA",
    label: "Metadata",
    filename: "meta.json",
    type: "NLP",
  },
  {
    id: "QA_DATA",
    label: "Training Dataset",
    type: "QA",
    filename: "openai_dataset.jsonl",
  },
  {
    id: "QA_TEST_DATA",
    label: "Validation Dataset",
    type: "QA",
    filename: "test_openai_dataset.jsonl",
  },
  {
    id: "META_DATA",
    label: "Metadata",
    type: "QA",
    filename: "metrics.json",
  },
  {
    id: "GN_DATA",
    label: "Training Dataset",
    type: "GN",
    filename: "genai_dataset.jsonl",
  },
  {
    id: "GN_TEST_DATA",
    label: "Validation Dataset",
    type: "GN",
    filename: "test_genai_dataset.jsonl",
  },
  {
    id: "GN_META_DATA",
    label: "Metadata",
    type: "GN",
    filename: "metrics.json",
  },
  {
    id: "CN_DATA",
    label: "Training Dataset",
    type: "CN",
    filename: "convai_dataset.jsonl",
  },
  {
    id: "CN_TEST_DATA",
    label: "Validation Dataset",
    type: "CN",
    filename: "test_convai_dataset.jsonl",
  },
  {
    id: "CN_META_DATA",
    label: "Metadata",
    type: "CN",
    filename: "metrics.json",
  },
  {
    id: "GN_LORA_ADAPTER",
    label: "LoRA Adapter (safetensors)",
    type: "GN",
    filename: "lora_adapter/adapter_model.safetensors",
  },
  {
    id: "GN_LORA_CONFIG",
    label: "LoRA Config (JSON)",
    type: "GN",
    filename: "lora_adapter/adapter_config.json",
  },
  {
    id: "GN_FULL_MODEL",
    label: "Full Model (tar.gz)",
    type: "GN",
    filename: "model.tar.gz",
  },
  {
    id: "CN_LORA_ADAPTER",
    label: "LoRA Adapter (safetensors)",
    type: "CN",
    filename: "lora_adapter/adapter_model.safetensors",
  },
  {
    id: "CN_LORA_CONFIG",
    label: "LoRA Config (JSON)",
    type: "CN",
    filename: "lora_adapter/adapter_config.json",
  },
  {
    id: "CN_FULL_MODEL",
    label: "Full Model (tar.gz)",
    type: "CN",
    filename: "model.tar.gz",
  },
];

const BATCH_DOWNLOAD_OPTIONS = [
  {
    id: "OUTPUT_DATA",
    label: "Output",
    filename: "output.csv",
    type: "ALL",
  },
  {
    id: "META_DATA",
    label: "Metadata",
    filename: "metadata.json",
    type: "ALL",
  },
  // {
  //   id: "INPUT_DATA",
  //   label: "Input",
  //   filename: "input.csv",
  //   type: "ALL",
  // },
];

const NOTIFICATION_OPTIONS = [
  {
    id: "NOTIFICATION_ON_TR_COMPLETED",
    label: "Email on completed",
    description: "admin.notifications.emailOnCompletedDesc",
    logicalPropertyName: "emailOnTrainingCompleted",
    type: "TR",
  },
  {
    id: "NOTIFICATION_ON_TR_FAILED",
    label: "Email on failed",
    description: "admin.notifications.emailOnFailedDesc",
    logicalPropertyName: "emailOnTrainingFailed",
    type: "TR",
  },
  {
    id: "NOTIFICATION_ON_TR_START",
    label: "Email on start",
    description: "admin.notifications.emailOnStartDesc",
    logicalPropertyName: "emailOnTrainingStart",
    type: "TR",
  },
  {
    id: "NOTIFICATION_ON_BT_COMPLETED",
    label: "Email on completed",
    description: "admin.notifications.benchmarkCompletedDesc",
    logicalPropertyName: "emailOnBatchCompleted",
    type: "BT",
  },
  {
    id: "NOTIFICATION_ON_BT_FAILED",
    label: "Email on failed",
    description: "admin.notifications.benchmarkFailedDesc",
    logicalPropertyName: "emailOnBatchFailed",
    type: "BT",
  },
  {
    id: "NOTIFICATION_ON_BT_START",
    label: "Email on start",
    description: "admin.notifications.benchmarkStartDesc",
    logicalPropertyName: "emailOnBatchStart",
    type: "BT",
  },
  {
    id: "NOTIFICATION_TO_ALL_USERS",
    label: "Notify all users in workspace",
    description: "admin.notifications.notifyAllUsersDesc",
    logicalPropertyName: "notifyAllUsersInWorkspace",
    type: "TR",
  },
  {
    id: "NOTIFICATION_ON_API_CONSUMPTION",
    label: "Notify consumption",
    description: "admin.notifications.notifyConsumptionDesc",
    logicalPropertyName: "notifyOnThreasholdConsumption",
    type: "PR",
    disabledIfBase: false,
    showSlider: true,
    sliderPropertyName: "notificationThresholdConsumption",
  },
];

const supportDomain = "https://support.toothfairyai.com";
const supportUrl = `https://toothfairyai.github.io/tooth-fairy-mono-repo/docs/intro`;
const initialEditorValue = [
  {
    children: [
      {
        text: "",
      },
    ],
    type: "paragraph",
  },
];
const baseModels = [
  {
    id: "meta-llama/Llama-4-Maverick-17B-128E-Instruct",
    type: "conversational",
    label: "Llama 4 Maverick (Serverless LoRA)",
    maxBatchSize: 8,
  },
  {
    id: "meta-llama/Meta-Llama-3.1-70B-Instruct-Reference",
    type: "conversational",
    label: "Llama 3.1 70B Instruct (Serverless LoRA)",
    maxBatchSize: 8,
  },
  {
    id: "meta-llama/Meta-Llama-3.1-8B-Instruct-Reference",
    type: "conversational",
    label: "Llama 3.1 8B Instruct (Serverless LoRA)",
    maxBatchSize: 32,
  },
  {
    id: "deepseek-ai/DeepSeek-R1-Distill-Llama-70B",
    type: "conversational",
    label: "DeepSeek R1 Distill Llama 70B",
    maxBatchSize: 8,
  },
  {
    id: "deepseek-ai/DeepSeek-R1-Distill-Qwen-14B",
    type: "conversational",
    label: "DeepSeek R1 Distill Qwen 14B",
    maxBatchSize: 40,
  },
  {
    id: "deepseek-ai/DeepSeek-R1-Distill-Qwen-1.5B",
    type: "conversational",
    label: "DeepSeek R1 Distill Llama 1.5B",
    maxBatchSize: 40,
  },
  {
    id: "meta-llama/Llama-3.3-70B-Instruct-Reference",
    type: "conversational",
    label: "Llama 3.3 70B Instruct",
    maxBatchSize: 8,
  },
  {
    id: "meta-llama/Llama-3.2-3B-Instruct",
    type: "conversational",
    label: "Llama 3.2 3B Instruct",
    maxBatchSize: 32,
  },
  {
    id: "meta-llama/Llama-3.2-1B-Instruct",
    type: "conversational",
    label: "Llama 3.2 1B Instruct",
    maxBatchSize: 32,
  },

  {
    id: "Qwen/Qwen2.5-14B-Instruct",
    type: "conversational",
    label: "Qwen 2.5 14B Instruct",
    maxBatchSize: 32,
  },
  {
    id: "Qwen/Qwen2.5-72B-Instruct",
    type: "conversational",
    label: "Qwen 2.5 72B Instruct",
    maxBatchSize: 16,
  },
  {
    id: "accounts/fireworks/models/qwen2p5-vl-3b-instruct",
    type: "conversational",
    label: "Qwen 2.5 VL 3B Instruct",
    maxBatchSize: 32,
  },
  {
    id: "accounts/fireworks/models/qwen2p5-vl-7b-instruct",
    type: "conversational",
    label: "Qwen 2.5 VL 7B Instruct",
    maxBatchSize: 32,
  },
  {
    id: "accounts/fireworks/models/qwen2p5-vl-32b-instruct",
    type: "conversational",
    label: "Qwen 2.5 VL 32B Instruct",
    maxBatchSize: 16,
  },
  {
    id: "accounts/fireworks/models/qwen2p5-vl-72b-instruct",
    type: "conversational",
    label: "Qwen 2.5 VL 72B Instruct",
    maxBatchSize: 8,
  },
];
questionsLimit = 10;
learningRatGN = 0.001;
baseThreasholdConsumption = 90;
maxQueueWaitTime = 180;
maxStepsNLP = 40000;
patienceNLP = 1600;
baseTrainingNLPEpochs = 200;
baseTrainingRCEpochs = 3;
baseTrainingGNEpochs = 4;
batchSizeNLP = 8;
batchSizeRC = 8;

batchSizeGN = 8;
learningRateNLP = 0.0001;
learningRateRC = 0.0001;
learningRatGN = 0.0001;
testSizeNLP = 0.1;
testSizeRC = 0.1;
testSizeGN = 0.1;
datasetTestSizeThresholdNLP = 200;
datasetTestSizeThresholdRC = 200;
datasetTestSizeThresholdGN = 200;
temperature = 0.3;
topP = 1;
maxTokens = 800;
presencePenalty = 0.5;
frequencyPenalty = 0.5;
docTopK = 4;
qaDocTopK = 4;
isAIQuestionsEnabled = false;
isShowRequestEnabled = false;
isAnswerByNEREnabled = false;
showProgrammaticChats = false;
showCost = false;
loraR = 8;
loraAlpha = 8;
loraDropout = 0.1;
evaluationNumber = 20;
checkpointsNumber = 1;
peakLearningRate = 0;
warmUpLearningRate = 0;
dpoBetaGN = 0.1;
trainOnInputs = "auto";
loraLayers = null;

const TRAINING_TYPES = [
  {
    key: "conversationalTraining",
    short: "SFT",
    title: "Supervised finetuning",
  },
  {
    key: "generativeTraining",
    short: "DPO",
    title: "Direct preference optimisation",
  },
  {
    key: "grpoTraining",
    short: "GRPO",
    title: "Reinforcement learning (GRPO)",
  },
  {
    // Compound key: the training-log type MUST be grpoTraining (backend
    // routing); RL mode is signalled via trainingConfig.rl.enabled.
    // TrainingScreenContainer translates this key accordingly.
    key: "grpoTraining+rlvr",
    short: "RLVR",
    title: "RL with verifiable rewards",
  },
  {
    key: "ktoTraining",
    short: "KTO",
    title: "Binary feedback (KTO)",
  },
  {
    key: "continuedPretraining",
    short: "CPT",
    title: "Continued pretraining",
  },
];

const DOCUMENT_TYPES = [
  {
    key: "conversationalTraining",
    short: "SFT",
    label: "Conversations",
    labelKey: "documents.types.conversations",
    methodsKey: "documents.types.conversationsMethods",
    descKey: "documents.types.conversationsDesc",
    iconKey: "quantumChat",
  },
  {
    key: "generativeTraining",
    short: "DPO",
    label: "Preferences",
    labelKey: "documents.types.preferences",
    methodsKey: "documents.types.preferencesMethods",
    descKey: "documents.types.preferencesDesc",
    iconKey: "preferences",
  },
  {
    key: "rlTask",
    short: "RLVR",
    label: "RL tasks",
    labelKey: "documents.types.rlTasks",
    methodsKey: "documents.types.rlTasksMethods",
    descKey: "documents.types.rlTasksDesc",
    iconKey: "quantumChat",
  },
];

const BATCH_TYPES = [
  {
    key: "bm",
    short: "BM",
    label: "Standard",
  },
  {
    key: "abm",
    short: "ABM",
    label: "Antagonistic",
  },
];

export default {
  loraR,
  loraAlpha,
  loraDropout,
  evaluationNumber,
  temperature,
  topP,
  maxTokens,
  datasetTestSizeThresholdGN,
  mapKeysToNames,
  testSizeGN,
  onboardingData,
  config,
  learningRatGN,
  notificationConfig,
  defaultDocumentID,
  workspaceCreationData,
  initialWorkspaceValues,
  appButtonHeight,
  saveTimer,
  NLP_OPTIONS,
  countries,
  availableLanguages,
  sideMenuWidth,
  qaDocTopK,
  supportUrl,
  generateBaseIntentsFromBaseModel,
  screenResponsiveWidth,
  chartMaxHeight,
  doughnutChartMaxHeight,
  availableRCLanguages,
  supportDomain,
  inactiveTimeout,
  initialEditorValue,
  MRC_OPTIONS,
  questionsLimit,
  NOTIFICATION_OPTIONS,
  baseThreasholdConsumption,
  DOWNLOAD_OPTIONS,
  maxQueueWaitTime,
  maxStepsNLP,
  patienceNLP,
  baseTrainingNLPEpochs,
  baseTrainingRCEpochs,
  baseTrainingGNEpochs,
  learningRateNLP,
  testSizeNLP,
  learningRateRC,
  testSizeRC,
  batchSizeGN,
  datasetTestSizeThresholdNLP,
  datasetTestSizeThresholdRC,
  BATCH_DOWNLOAD_OPTIONS,
  frequencyPenalty,
  presencePenalty,
  isAIQuestionsEnabled,
  isShowRequestEnabled,
  isAnswerByNEREnabled,
  showProgrammaticChats,
  showCost,
  baseTopics,
  baseModels,
  defaultModerations,
  batchSizeRC,
  checkpointsNumber,
  peakLearningRate,
  warmUpLearningRate,
  trainOnInputs,
  loraLayers,
  dpoBetaGN,
  newsletterConsent,
  TRAINING_TYPES,
  DOCUMENT_TYPES,
  BATCH_TYPES,
};
